import { useCallback, useEffect, useState } from 'react';
import type { ActiveTimer } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import type { BillingBucket } from './buckets.ts';

// Every surface that can mutate the timer (header pill, ticket page, the
// popped-out mini window, another tab) pings this channel afterwards so the
// others re-read immediately instead of waiting out the 30s poll.
const CHANNEL = 't3-timer';

function broadcast(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const ch = new BroadcastChannel(CHANNEL);
  ch.postMessage('changed');
  ch.close();
}

export function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

export function elapsedMinutes(startedAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60_000));
}

/** "4d 8h 12m" — readable at the scale a forgotten timer reaches. */
export function formatDuration(minutes: number): string {
  const d = Math.floor(minutes / (24 * 60));
  const h = Math.floor((minutes % (24 * 60)) / 60);
  const m = minutes % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

export interface StartArgs {
  ticketId?: string;
  billingBucket?: BillingBucket;
  organizationId?: string;
  notes?: string;
}

export interface UseTimer {
  timer: ActiveTimer | null;
  /** null until the first fetch resolves — lets callers avoid a flash of "no timer". */
  loaded: boolean;
  busy: boolean;
  error: string | null;
  /**
   * The server refused to commit this timer's wall-clock elapsed time because
   * it has been running too long to be real. Until it is stopped with an
   * explicit duration (or discarded) nothing else can be started either.
   */
  runaway: boolean;
  clearError: () => void;
  refresh: () => Promise<void>;
  start: (args: StartArgs) => Promise<boolean>;
  /** `durationMinutes` overrides elapsed — required for a runaway timer. */
  stop: (durationMinutes?: number) => Promise<boolean>;
  cancel: () => Promise<boolean>;
  update: (patch: {
    billingBucket?: BillingBucket;
    organizationId?: string;
    notes?: string | null;
  }) => Promise<boolean>;
}

/**
 * Single source of truth for the running timer. Polls every 30s, ticks the
 * clock every second while running, and stays in sync with every other window
 * through a BroadcastChannel.
 */
export function useTimer(): UseTimer {
  const { agent } = useAuth();
  const [timer, setTimer] = useState<ActiveTimer | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runaway, setRunaway] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    if (!agent) {
      setTimer(null);
      setLoaded(true);
      return;
    }
    try {
      const r = await apiFetch<{ timer: ActiveTimer | null }>('/api/timer');
      setTimer(r.timer);
    } catch {
      // Leave the last known value in place — a transient network blip
      // shouldn't make a running timer vanish from the UI.
    } finally {
      setLoaded(true);
    }
  }, [agent]);

  // Initial load + 30s poll.
  useEffect(() => {
    if (!agent) {
      setTimer(null);
      setLoaded(true);
      return;
    }
    void refresh();
    const i = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(i);
  }, [agent, refresh]);

  // Cross-window sync.
  useEffect(() => {
    if (!agent || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHANNEL);
    ch.onmessage = () => void refresh();
    return () => ch.close();
  }, [agent, refresh]);

  // 1s clock while a timer runs.
  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [timer]);

  // Re-read when the window regains focus — a timer may have been stopped
  // elsewhere while this window sat in the background.
  useEffect(() => {
    if (!agent) return;
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [agent, refresh]);

  const run = useCallback(
    async (fn: () => Promise<void>, fallbackMessage: string): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        await refresh();
        broadcast();
        setRunaway(false);
        return true;
      } catch (e) {
        setError(e instanceof ApiError ? e.message : fallbackMessage);
        // Both codes mean the same thing to the UI: a clock has been left
        // running and has to be resolved with a real duration before anything
        // else can happen.
        if (e instanceof ApiError && (e.code === 'timer_runaway' || e.code === 'prior_timer_runaway')) {
          setRunaway(true);
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const start = useCallback(
    (args: StartArgs) =>
      run(async () => {
        await apiFetch('/api/timer/start', { method: 'POST', json: args });
      }, 'Failed to start timer.'),
    [run],
  );

  const stop = useCallback(
    (durationMinutes?: number) =>
      run(async () => {
        await apiFetch('/api/timer/stop', {
          method: 'POST',
          json: durationMinutes === undefined ? {} : { durationMinutes },
        });
      }, 'Failed to stop timer.'),
    [run],
  );

  const cancel = useCallback(
    () =>
      run(async () => {
        await apiFetch('/api/timer', { method: 'DELETE' });
      }, 'Failed to discard timer.'),
    [run],
  );

  const update = useCallback(
    (patch: { billingBucket?: BillingBucket; organizationId?: string; notes?: string | null }) =>
      run(async () => {
        await apiFetch('/api/timer', { method: 'PATCH', json: patch });
      }, 'Failed to update timer.'),
    [run],
  );

  return {
    timer,
    loaded,
    busy,
    error,
    runaway,
    clearError: () => setError(null),
    refresh,
    start,
    stop,
    cancel,
    update,
  };
}
